'use client';

import { useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useMemo, useState } from 'react';
import { AgentLineGenerateImage } from '@/app/components/icons/AgentLineGenerateImage';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { MAX_IMAGE_FILE_SIZE } from '@/app/constants';
import { useDevice, useFetchRecreateData, useGlobalModal, useRunTempUploadBatch } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { CreationWithTask, FormImagePrompt, ImageUploadFile, TaskInfo, UploadImageType } from '@/app/types';
import { getBackfillImageId } from '@/app/utils/client';
import { useBackfillData } from '../../_hooks/use-backfill-data';
import { CreationSelectorDialog } from '../_components/CreationSelectorDialog';
import { ToolFormContainer } from '../_components/ToolFormContainer';
import { ToolFormSubmitButton } from '../_components/ToolFormSubmitButton';
import { ToolMediaUpload } from '../_components/ToolMediaUpload';
import { ToolMediaUploadHeader } from '../_components/ToolMediaUploadHeader';
import { useToolForm } from '../_hooks/use-tool-form';
import {
  applyToolFormParamsPatch,
  buildToolTaskRecreatePatch,
  createImagePromptFromCreation,
  createPromptListHelpers,
  createToolImagePromptFromUploadFile,
} from '../_utils';
import { getToolTempImagesPendingItems } from '../_utils/tool-temp-upload-items';
import { StyleSelector, type StyleSelectorOption } from './_components/StyleSelector';
import { StyleTransferSettings } from './_components/StyleTransferSettings';

const StyleTransferForm: FC = () => {
  const t = useTranslations();
  const { isMobile } = useDevice();
  const storeApi = useStoreApi();

  const { settings, updateSettings } = useToolForm('style_transfer');

  const [openImageSelector, setOpenImageSelector] = useState(false);
  const [selectedImageCreationIndex, setSelectedImageCreationIndex] = useState<number | null>(null);
  const [selectedImageCreation, setSelectedImageCreation] = useState<CreationWithTask | undefined>();

  const { open: openSimpleCreationDetailModal } = useGlobalModal('simpleCreationDetailModal');

  const { updateStyleTransferFormParams, imagePrompts, textPrompt, remixStyle, userInfo } = useStore((state) => ({
    updateStyleTransferFormParams: state.updateStyleTransferFormParams,
    imagePrompts: state.styleTransferFormParams.imagePrompts,
    textPrompt: state.styleTransferFormParams.textPrompt,
    remixStyle: state.styleTransferFormParams.remixStyle,
    userInfo: state.userInfo,
  }));

  const imagePrompt = imagePrompts?.[0] || null;

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp images',
    (refs) =>
      getToolTempImagesPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().styleTransferFormParams.imagePrompts,
          (prompts) => updateStyleTransferFormParams('imagePrompts', prompts.filter(Boolean) as FormImagePrompt[]),
        ),
        { imagePrompts },
      ),
    [imagePrompts, updateStyleTransferFormParams, userInfo],
  );

  const handleTaskSuccess = useCallback(
    async (task: TaskInfo, inspirationId?: string, source?: string) => {
      const patch = await buildToolTaskRecreatePatch({
        tool: 'style_transfer',
        task,
        inspirationId,
        inspirationFrom: source,
      });
      applyToolFormParamsPatch(updateStyleTransferFormParams, patch);
    },
    [updateStyleTransferFormParams],
  );

  useFetchRecreateData({
    onTaskSuccess: handleTaskSuccess,
  });

  const doBackFill = useMemoizedFn(({ imagePrompt }: { timestamp: number; imagePrompt: UploadImageType }) => {
    updateStyleTransferFormParams('imagePrompts', [
      {
        id: getBackfillImageId(0),
        status: 'done',
        prompt: imagePrompt,
      },
    ]);
  });

  useBackfillData({
    doBackFill,
  });

  const handleSelectImage = useCallback((index: number | null, creation?: CreationWithTask) => {
    if (!creation) {
      setSelectedImageCreationIndex(null);
      setSelectedImageCreation(undefined);
      return;
    }

    setSelectedImageCreationIndex(index);
    setSelectedImageCreation(creation);
  }, []);

  const handleRemoveImage = useCallback(() => {
    updateStyleTransferFormParams('imagePrompts', []);
  }, [updateStyleTransferFormParams]);

  const handleImageUpload = useMemoizedFn((file: ImageUploadFile) => {
    const imagePrompt = createToolImagePromptFromUploadFile(file);
    updateStyleTransferFormParams('imagePrompts', [imagePrompt]);

    if (file.status === 'error' || file.status === 'removed') {
      updateStyleTransferFormParams('imagePrompts', []);
    }
  });

  const getSelectedImage = useCallback(async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = URL.createObjectURL(blob);
      });
      return { blob, width: img.width, height: img.height };
    } catch {
      return null;
    }
  }, []);

  const handleTextPromptChange = useCallback(
    (value: string) => {
      updateStyleTransferFormParams('textPrompt', value);
    },
    [updateStyleTransferFormParams],
  );

  const handleStyleChange = useCallback(
    (style: StyleSelectorOption | undefined) => {
      updateStyleTransferFormParams(
        'remixStyle',
        style
          ? {
              id: style.id,
              name: style.name,
              url: style.image_url,
            }
          : undefined,
      );
    },
    [updateStyleTransferFormParams],
  );

  const hasUploadingMedia = imagePrompts.some((prompt) => prompt.status === 'uploading');
  const submitDisabledTip = useMemo(() => {
    if (!imagePrompt || imagePrompt.status !== 'done') return t('tools.styleTransfer.error.sourceImageRequired');
    if (!textPrompt?.trim() && !remixStyle) return t('tools.styleTransfer.error.promptOrStyleRequired');
    if (hasUploadingMedia) return t('create.createForm.error.uploadingMedia');
    return undefined;
  }, [hasUploadingMedia, imagePrompt, remixStyle, t, textPrompt]);

  const remainingImageSlots = imagePrompt ? 0 : 1;

  return (
    <>
      <ToolFormContainer
        title={t('tools.styleTransfer.title')}
        description={t('tools.styleTransfer.description')}
        mediaSection={
          <MediaDropZone
            acceptVideo={false}
            maxImageCount={remainingImageSlots}
            disabled={remainingImageSlots === 0}
            maxImageFileSize={MAX_IMAGE_FILE_SIZE}
            getSelectedImage={getSelectedImage}
            allowTempMode={true}
            enablePaste={true}
            onChange={(file) => {
              handleImageUpload(file as ImageUploadFile);
            }}
          >
            <div className="flex w-full flex-col gap-4">
              <ToolMediaUpload
                type="image"
                header={
                  <ToolMediaUploadHeader
                    icon={<AgentLineGenerateImage className="size-5 text-system-white" />}
                    title={t('tools.styleTransfer.sourceImage')}
                  />
                }
                imagePrompt={imagePrompt}
                isMobile={isMobile}
                maxFileSize={MAX_IMAGE_FILE_SIZE}
                getSelectedImage={getSelectedImage}
                onImageUpload={handleImageUpload}
                onOpenSelector={() => setOpenImageSelector(true)}
                onRemoveMedia={handleRemoveImage}
              />

              <StyleSelector
                textPrompt={textPrompt || ''}
                onTextPromptChange={handleTextPromptChange}
                selectedStyle={remixStyle?.id}
                selectedStyleName={remixStyle?.name}
                onStyleChange={handleStyleChange}
              />

              <StyleTransferSettings
                aspectRatio={settings.aspect_ratio as string}
                resolution={settings.resolution as string}
                sampleCount={settings.sample_count as number}
                onAspectRatioChange={(value) => updateSettings({ aspect_ratio: value })}
                onResolutionChange={(value) => updateSettings({ resolution: value })}
                onSampleCountChange={(value) => updateSettings({ sample_count: value })}
              />
            </div>
          </MediaDropZone>
        }
        submitButton={
          <ToolFormSubmitButton taskType="style_transfer" disabledTip={submitDisabledTip}>
            {t('create.createForm.submit')}
          </ToolFormSubmitButton>
        }
      />

      <CreationSelectorDialog
        open={openImageSelector}
        onOpenChange={setOpenImageSelector}
        selectedIndex={selectedImageCreationIndex}
        mediaType="image"
        onSelectionChange={handleSelectImage}
        onConfirm={() => {
          setOpenImageSelector(false);
          if (selectedImageCreation) {
            updateStyleTransferFormParams('imagePrompts', [
              createImagePromptFromCreation(selectedImageCreation, { includeReference: true }),
            ]);
          }
        }}
        onViewCreation={(creation) => {
          openSimpleCreationDetailModal({
            creation,
          });
        }}
      />
    </>
  );
};

export default StyleTransferForm;
